import React, { forwardRef } from 'react';
import styled from 'styled-components';
import IEditableProps from '../../utilities/IEditableProps';
import IStyledProps from 'src/utilities/IStyledProps';
import classNames from 'classnames';
import parse from "html-react-parser";

export interface IOverlayProps extends IEditableProps, IStyledProps {
    title: string;
    titleAs?: string;
    subtitle: string;
    subtitleAs?: string;
    body: string;
    color: string;
    headerClassName: string;
    type?: string;
    children;
}

function Header({ title, titleAs, subtitle, subtitleAs, headerClassName, type }) {
    if (type === "header1") {
        const Title = titleAs || "h1";
        const SubTitle = subtitleAs || "h2";

        return (
            <>
                <Title className={classNames("card-title", headerClassName)} dangerouslySetInnerHTML={{ __html: title }} />
                <SubTitle className="card-subtitle mb-4 text-serif" dangerouslySetInnerHTML={{ __html: subtitle }} />
            </>
        );
    } else {
        const Title = titleAs || "h2";
        const SubTitle = subtitleAs || "h3";

        return (
            <>
                <Title className={classNames("card-title mb-3 mb-lg-2 text-serif", headerClassName)} dangerouslySetInnerHTML={{ __html: title }}/>
                <SubTitle className="card-subtitle mb-4" dangerouslySetInnerHTML={{ __html: subtitle }} />
            </>
        );
    }
}

const Overlay = forwardRef((props: IOverlayProps, ref: any) => {
    const { title, titleAs, subtitle, subtitleAs, body, children, className, editorClassObj, type, cmsId, headerClassName } = props;

    function getReplacedContent(elements) {
        return React.Children.map(elements, child => {
            if (child.props?.children && typeof child.props?.children === "string") {
                return React.cloneElement(child, child.props, child.props.children.replace(/Ϲ/g, "{").replace(/Ͻ/g, "}"));
            } else {
                return child;
            }
        });
    }

    function parseHTML(html: string) {
        if (!html) return null;
        try {
            return parse(html);
        } catch (e) {
            console.error('Overlay widget error:', e);
            return <div className="error-message">Error: Invalid html in "body" for Overlay widget: {e.message}</div>;
        }
    }

    return (
        <div data-id={cmsId} ref={ref} className={classNames("card overlay text-white rounded-0", type !== 'header1' ? 'small-overlay' : null, className, editorClassObj)}>
            <div className={"card-header p-0 border-0"}>
                <Header
                    title={title}
                    titleAs={titleAs}
                    subtitle={subtitle}
                    subtitleAs={subtitleAs}
                    headerClassName={headerClassName}
                    type={type}
                />
            </div>
            <div className="card-body p-0 mb-3">
                <div className="card-text">{typeof body === 'string' ? parseHTML(body) : getReplacedContent(body)}</div>
            </div>
            <div className="card-footer text-muted border-0 p-0 d-flex align-items-center flex-wrap">
                {getReplacedContent(children)}
            </div>
        </div>
    );
});

function getOverlayBackgroundColor(color) {
    switch (color) {
        case "gray":
            return "rgba(145, 130, 124, 0.87)";
        case "blue":
            return "rgba(36, 100, 139, 0.87)";
        case "yellow":
        case "deli mustard":
            return "rgba(255, 167, 36, 0.87)";
        case "green":
            return "rgba(48, 87, 75, 0.87)";
        case "red":
        default:
            return "rgba(198, 0, 36, 0.87)";
    }
}

function getButtonHoverColor(color) {
    switch (color) {
        case "gray":
            return "rgba(145, 130, 124, 1)";
        case "blue":
            return "rgba(36, 100, 139, 1)";
        case "yellow":
        case "deli mustard":
            return "rgba(255, 167, 36, 1)";
        case "green":
            return "rgba(48, 87, 75, 1)";
        case "red":
        default:
            return "rgba(198, 0, 36, 1)";
    }
}

export default styled(Overlay)`
    border: none;
    &.card{
        background-color: ${props => getOverlayBackgroundColor(props.color)};

        .btn.btn-outline-light {
            &:hover, &:focus {
                color: white;
                background-color: ${props => getButtonHoverColor(props.color)} !important;
            }
        }

        h6 {
            margin-bottom: 1rem;
        }

        a {
            color: white;
        }
    }

    &.small-overlay {

        h2.card-title {
            font-size: 1.611rem;
            margin-bottom: 5px;
        }
        h3.card-subtitle {
            font-size: 1.5rem;
            font-family: "National2", sans serif;
            font-weight: 700;
        }
        a, p {
            font-size: 0.889rem;
        }
    }
`;